import type { Client } from '@frontend/api-types';
import { convertObjectKeysCase } from '@frontend/duck-tape';
import { getMetaCookies } from '@frontend/web-utils';
import type { AnalyticsBrowser } from '@segment/analytics-next';

/**
 * This hook should never be used directly. It should only ever be used by the `useAnalytics` hook from the `@frontend/react` package.
 */
export const useSegment = ({
  analytics,
  env,
}: {
  analytics: ReturnType<typeof AnalyticsBrowser.load>;
  env: EnvType;
}) => {
  const track = (object: string, action: string, data: Record<string, unknown> = {}, client?: Client) => {
    const clientData = client ? { clientId: client.id } : {};
    const metaCookies = getMetaCookies();
    const snakeCasedData = {
      ...convertObjectKeysCase(clientData, 'snakeCase'),
      ...convertObjectKeysCase(data, 'snakeCase'),
      ...metaCookies,
    };
    const label = `${object} ${action}`;
    if (env === 'local') {
      // eslint-disable-next-line no-console
      console.info('Mock Segment track:', {
        action,
        data: snakeCasedData,
        label,
      });
      return;
    }
    analytics.track(label, snakeCasedData);
    return;
  };

  const trackRouteChange = (params: {
    category?: string;
    name?: string;
    properties?: {
      path?: string;
      referrer?: string;
      search?: Record<string, string>;
      searchStr?: string;
      title?: string;
      url?: string;
    };
  }) => {
    const metaCookies = getMetaCookies();
    const propertiesWithMeta = { ...params.properties, ...metaCookies };
    if (env === 'local') {
      // eslint-disable-next-line no-console
      console.info('Mock Segment page track:', { ...params, properties: propertiesWithMeta });
      return;
    }
    analytics.page(params.category, params.name, propertiesWithMeta);
    return;
  };

  return { track, trackRouteChange };
};
